import { LoginModal } from "@components/login-modal";
import { PlanChoiceModal } from "@components/plan-choice-modal";
import { QualityChoiceModal } from "@components/quality-choice-modal";
import { ResponsiveModal } from "@components/responsive-modal";
import { getPackPreview, usePacks } from "@features/images/use-packs";
import { useLoras } from "@features/loras/use-loras";
import { useUser } from "@features/user/use-user";
import { useEffectPlease } from "@features/utils";
import { CheckIcon } from "@heroicons/react/24/solid";
import { Heading, Spinner, Text, Tooltip } from "@radix-ui/themes";
import { LogoutHeader } from "@views/layout/header";
import { AnimatePresence, motion } from "framer-motion";
import posthog from "posthog-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { FallingPictures } from "./parts/falling-pictures";
import { OnboardingQuestions } from "./parts/onboarding-questions";
import { PreviewView } from "./upload/components/PreviewView";
import { UploadView } from "./upload/components/UploadView";
import {
  clearOnboardingState,
  DEFAULT_PACKS,
  getOnboardingState,
  isOnboardingStateValid,
  onboardingPackName,
  saveOnboardingState,
} from "./upload/hooks/use-onboarding";
import { useProcessOnboarding } from "./upload/hooks/use-process-onboarding";

// Re-export types for backwards compatibility
export type { OnboardingState } from "./upload/hooks/use-onboarding";
export { clearOnboardingState, getOnboardingState, saveOnboardingState };

// Local storage key for onboarding preferences (features, sources)
// Using persist_ prefix so it survives logout and login
const ONBOARDING_PREFS_KEY = "persist_onboarding_preferences";

export interface OnboardingPreferences {
  features: string[];
  sources: string[];
  timestamp: number;
}

// Save onboarding preferences to localStorage (persists until login)
export const saveOnboardingPreferences = (
  prefs: Partial<OnboardingPreferences>,
) => {
  try {
    const existing = getOnboardingPreferences();
    const updated = {
      ...existing,
      ...prefs,
      timestamp: Date.now(),
    };
    localStorage.setItem(ONBOARDING_PREFS_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error("Failed to save onboarding preferences:", e);
  }
};

// Get onboarding preferences from localStorage
export const getOnboardingPreferences = (): OnboardingPreferences => {
  try {
    const stored = localStorage.getItem(ONBOARDING_PREFS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Failed to parse onboarding preferences:", e);
  }
  return { features: [], sources: [], timestamp: 0 };
};

// Clear onboarding preferences (call after first login)
export const clearOnboardingPreferences = () => {
  localStorage.removeItem(ONBOARDING_PREFS_KEY);
};

// Check if preferences have been answered
export const hasOnboardingPreferences = (): boolean => {
  const prefs = getOnboardingPreferences();
  return prefs.features.length > 0 && prefs.sources.length > 0;
};

export const UploadIntro = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, credits } = useUser();
  const { loras } = useLoras();
  const isComplete = searchParams.get("complete") === "1";
  const isFreeMode =
    searchParams.get("free") === "1" ||
    credits > 100 ||
    user?.data?.plan !== "free";
  const isLoggedIn = !!user.data;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<string>(DEFAULT_PACKS[0]);
  const [isPackModalOpen, setIsPackModalOpen] = useState(false);
  const [isQualityModalOpen, setIsQualityModalOpen] = useState(!isFreeMode);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [showQuestions, setShowQuestions] = useState(
    () => !hasOnboardingPreferences(),
  );

  useEffectPlease(() => {
    if (!!loras?.data?.length) {
      document.location = "/";
    }
  }, [!!loras?.data?.length]);

  useEffect(() => {
    if (user?.data?.id) {
      setShowQuestions(false);
    }
  }, [user.data]);

  // Show quality modal if not in free mode
  useEffect(() => {
    if (!isFreeMode && !isComplete) {
      setIsQualityModalOpen(true);
    }
  }, [isFreeMode, isComplete]);

  // Handle file selection
  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    // Only accept JPEG, JPG, and PNG images
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setUploadedImage(dataUrl);
      // Save to session storage
      saveOnboardingState({ imageDataUrl: dataUrl });
      posthog.capture("onboarding_image_uploaded");
    };
    reader.readAsDataURL(file);
  }, []);

  // If complete=1, show importing view with water fill animation
  if (isComplete) {
    return <ImportingView />;
  }

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  // Helper function to show login modal or proceed directly
  const requireLoginOrProceed = (action: string) => {
    if (isLoggedIn) {
      navigate("/upload?free=1&complete=1");
    } else {
      setIsLoginModalOpen(true);
      posthog.capture("login_modal_opened", { action });
    }
  };

  // Handle successful login from modal
  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
    // After login, proceed to importing
    navigate("/upload?free=1&complete=1");
  };

  // Action handlers - show login modal (or process directly if logged in)
  const handleProfessional = () => {
    posthog.capture("onboarding_action_selected", {
      action: "professional",
      pack: selectedPack,
    });
    saveOnboardingState({
      selectedAction: "professional",
      selectedPack: selectedPack,
    });
    requireLoginOrProceed("professional");
  };

  const handleEdit = () => {
    posthog.capture("onboarding_action_selected", { action: "edit" });
    saveOnboardingState({ selectedAction: "edit" });
    requireLoginOrProceed("edit");
  };

  const handleTrain = () => {
    posthog.capture("onboarding_action_selected", { action: "train" });
    setIsQualityModalOpen(true);
  };

  // Reset to upload state
  const handleReset = () => {
    setUploadedImage(null);
    clearOnboardingState();
  };

  // If questions haven't been answered yet, show the questions overlay
  if (showQuestions) {
    return <QuestionsOverlay onComplete={() => setShowQuestions(false)} />;
  }

  return (
    <div
      className={twMerge(
        "min-h-dvh w-full bg-white dark:bg-slate-950 pb-20 md:pb-0",
      )}
    >
      {/* Hide content when quality modal is open in non-free mode */}
      {(!isQualityModalOpen || isFreeMode) && (
        <>
          <LogoutHeader />

          <div
            className="max-w-4xl mx-auto px-4 py-8 flex flex-col justify-center"
            style={{ minHeight: "calc(100dvh - 80px)" }}
          >
            <AnimatePresence mode="wait">
              {!uploadedImage ? (
                <UploadView
                  key="upload"
                  isDragOver={isDragOver}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onFileSelect={() => fileInputRef.current?.click()}
                />
              ) : (
                <PreviewView
                  key="preview"
                  imageUrl={uploadedImage}
                  selectedPack={selectedPack}
                  onChangePack={() => {
                    posthog.capture("onboarding_pack_modal_opened");
                    setIsPackModalOpen(true);
                  }}
                  onProfessional={handleProfessional}
                  onEdit={handleEdit}
                  onTrain={handleTrain}
                  onReset={handleReset}
                />
              )}
            </AnimatePresence>
          </div>
        </>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {/* Pack selection modal */}
      <PackSelectionModal
        isOpen={isPackModalOpen}
        onClose={() => setIsPackModalOpen(false)}
        selectedPack={selectedPack}
        onSelectPack={(pack) => {
          posthog.capture("onboarding_pack_selected", { pack });
          setSelectedPack(pack);
          setIsPackModalOpen(false);
        }}
      />

      {/* Plan choice modal (forced) or Quality choice modal (optional) */}
      {!isFreeMode ? (
        <PlanChoiceModal
          isOpen={isQualityModalOpen}
          onClose={() => {}}
          hideCloseButton={true}
        />
      ) : (
        <QualityChoiceModal
          isOpen={isQualityModalOpen}
          onClose={() => {
            setIsQualityModalOpen(false);
          }}
          hideCloseButton={false}
          onContinueStandard={() => {
            setIsQualityModalOpen(false);
            // Redirect to free mode
            navigate("/upload?free=1");
          }}
          onAddMorePictures={() => {
            saveOnboardingState({ selectedAction: "train" });
            setIsQualityModalOpen(false);
            requireLoginOrProceed("train");
          }}
          onUpgrade={() => {
            saveOnboardingState({ selectedAction: "train" });
            setIsQualityModalOpen(false);
            requireLoginOrProceed("train");
          }}
        />
      )}

      {/* Login modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
};

// Importing view with water fill animation
const ImportingView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAnalytics } = useUser();
  const { processOnboardingAction, navigateToResult } = useProcessOnboarding();
  const [fillProgress, setFillProgress] = useState(0);
  const [importComplete, setImportComplete] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const hasStartedImport = useRef(false);

  // Store image URL in state so it persists after onboarding state is cleared
  const [imageUrl] = useState(() => getOnboardingState().imageDataUrl);

  // Animation: Fill up over 5 seconds during import
  useEffect(() => {
    if (!isOnboardingStateValid()) {
      // No valid state, redirect to start
      navigate("/upload?free=1");
      return;
    }

    // Prevent duplicate import calls
    if (hasStartedImport.current) {
      return;
    }
    hasStartedImport.current = true;

    // Start the import process
    const doImport = async () => {
      const state = getOnboardingState();
      posthog.capture("onboarding_import_started", {
        action: state.selectedAction,
      });
      const result = await processOnboardingAction(state);

      if (result.success && result.navigateTo) {
        // Send onboarding preferences (features, sources) to backend
        const prefs = getOnboardingPreferences();
        if (prefs.features.length > 0 || prefs.sources.length > 0) {
          await setAnalytics({
            features: prefs.features,
            sources: prefs.sources,
          });
          clearOnboardingPreferences();
        }

        posthog.capture("onboarding_import_completed", {
          action: state.selectedAction,
        });
        setNavigateTo(result.navigateTo);
        setImportComplete(true);
      } else if (!result.success && result.error !== "Already processing") {
        posthog.capture("onboarding_import_error", {
          action: state.selectedAction,
          error: result.error,
        });
        setError(result.error || "Something went wrong");
        // If failed, redirect after a short delay
        setTimeout(() => navigate("/upload?free=1"), 2000);
      }
    };
    doImport();

    // Start fill animation (5 seconds to 80%)
    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const duration = 5000; // 5 seconds
      const maxProgress = 80; // Stop at 80% until import completes

      const progress = Math.min(
        (elapsed / duration) * maxProgress,
        maxProgress,
      );
      setFillProgress(progress);

      if (progress < maxProgress) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When import completes, finish the animation (80% to 100% in 3 seconds)
  useEffect(() => {
    if (importComplete && !isFinishing && navigateTo) {
      setIsFinishing(true);
      const startProgress = fillProgress;
      const startTime = Date.now();
      const duration = 7000; // 7 seconds

      const finishAnimation = () => {
        const elapsed = Date.now() - startTime;
        const progress =
          startProgress + ((100 - startProgress) * elapsed) / duration;

        if (progress >= 100) {
          setFillProgress(100);
          // Animation complete, now navigate
          setTimeout(() => {
            navigateToResult(navigateTo);
          }, 200);
          return;
        }

        setFillProgress(progress);
        requestAnimationFrame(finishAnimation);
      };

      requestAnimationFrame(finishAnimation);
    }
  }, [importComplete, isFinishing, fillProgress, navigateTo, navigateToResult]);

  return (
    <div className="min-h-dvh w-full bg-white dark:bg-slate-950 flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center space-y-8"
      >
        {/* Image with water fill effect */}
        <div className="relative inline-block">
          {/* Container with rounded corners and shadow */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            {/* Base image (grayscale) */}
            {imageUrl && (
              <img
                src={imageUrl}
                alt="Importing"
                className="max-h-[50vh] max-w-[80vw] object-contain grayscale opacity-40"
              />
            )}

            {/* Colored overlay that fills up from bottom */}
            {imageUrl && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{
                  clipPath: `inset(${100 - fillProgress}% 0 0 0)`,
                }}
              >
                <img
                  src={imageUrl}
                  alt="Importing"
                  className="max-h-[50vh] max-w-[80vw] object-contain"
                />
                {/* Water wave effect at the top of the fill */}
                <div
                  className="absolute left-0 right-0 h-4 pointer-events-none"
                  style={{
                    bottom: `${fillProgress}%`,
                    background:
                      "linear-gradient(to bottom, rgba(99, 102, 241, 0.3), transparent)",
                  }}
                >
                  <svg
                    className="absolute -top-2 left-0 w-full h-4"
                    viewBox="0 0 1200 30"
                    preserveAspectRatio="none"
                  >
                    <motion.path
                      d="M0,15 Q150,0 300,15 T600,15 T900,15 T1200,15 L1200,30 L0,30 Z"
                      fill="rgba(99, 102, 241, 0.2)"
                      animate={{
                        d: [
                          "M0,15 Q150,0 300,15 T600,15 T900,15 T1200,15 L1200,30 L0,30 Z",
                          "M0,15 Q150,30 300,15 T600,15 T900,15 T1200,15 L1200,30 L0,30 Z",
                          "M0,15 Q150,0 300,15 T600,15 T900,15 T1200,15 L1200,30 L0,30 Z",
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  </svg>
                </div>
              </div>
            )}

            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
          </div>
        </div>

        {/* Progress text */}
        <div className="space-y-2">
          <Heading size="5" className="text-slate-900 dark:text-white">
            {error
              ? t("onboarding.importing.error_title", "Import failed")
              : t("onboarding.importing.title", "Preparing your image...")}
          </Heading>
          <Text className="text-slate-500 dark:text-slate-400 block">
            {error
              ? error
              : t(
                  "onboarding.importing.subtitle",
                  "This will only take a moment",
                )}
          </Text>
        </div>

        {/* Progress bar */}
        <div className="w-64 mx-auto">
          <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"
              style={{ width: `${fillProgress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
          <Text className="text-xs text-slate-400 mt-2 block">
            {Math.round(fillProgress)}%
          </Text>
        </div>
      </motion.div>

      {/* Add shimmer animation style */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

// Pack selection modal
export const PackSelectionModal = ({
  isOpen,
  onClose,
  selectedPack,
  onSelectPack,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedPack: string;
  onSelectPack: (pack: string) => void;
}) => {
  const { t } = useTranslation();
  const { packs } = usePacks();
  const allPacks = packs.data || [];

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("onboarding.pack_modal.title")}
      height="auto"
      maxHeight="80vh"
    >
      <div className="space-y-4">
        <Text className="text-slate-600 dark:text-slate-400 block">
          {t("onboarding.pack_modal.subtitle")}
        </Text>

        {/* Available packs (unlocked) */}
        <div className="space-y-2">
          <Text className="font-medium block">
            {t("onboarding.pack_modal.available")}
          </Text>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-2">
            {DEFAULT_PACKS.map((packId) => (
              <PackTile
                key={packId}
                packId={packId}
                name={onboardingPackName[packId]}
                isSelected={selectedPack === packId}
                isLocked={false}
                onClick={() => onSelectPack(packId)}
              />
            ))}
          </div>
        </div>

        {/* Other packs (locked) */}
        {allPacks.filter((p) => !DEFAULT_PACKS.includes(p.id)).length > 0 && (
          <div className="space-y-2 pt-4">
            <Text className="font-medium block text-slate-400">
              {t("onboarding.pack_modal.locked")}
            </Text>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-2">
              {allPacks
                .filter((p) => !DEFAULT_PACKS.includes(p.id))
                .slice(0, 32)
                .sort((a, b) => {
                  // a.categories_slug includes "professional_work" first
                  const aIsProfessional = a.categories_slug?.includes(
                    "professional_work",
                  )
                    ? 0
                    : 1;
                  const bIsProfessional = b.categories_slug?.includes(
                    "professional_work",
                  )
                    ? 0
                    : 1;
                  if (aIsProfessional !== bIsProfessional) {
                    return aIsProfessional - bIsProfessional;
                  }
                  // Then sort by name
                  return (onboardingPackName[a.id] || a.id).localeCompare(
                    onboardingPackName[b.id] || b.id,
                  );
                })
                .map((pack) => (
                  <PackTile
                    key={pack.id}
                    packId={pack.id}
                    name={t(["packs." + pack.id + ".name", pack.id])}
                    isSelected={false}
                    isLocked={true}
                    onClick={() => {}}
                  />
                ))}
            </div>
            <Text className="text-xs text-slate-400 block px-2 mx-auto">
              80+ more packs available
            </Text>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
};

// Pack tile component
const PackTile = ({
  packId,
  name,
  isSelected,
  isLocked,
  onClick,
}: {
  packId: string;
  name: string;
  isSelected: boolean;
  isLocked: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  const preview = getPackPreview(packId);

  return (
    <Tooltip
      content={isLocked ? t("onboarding.pack_modal.needs_subscription") : name}
    >
      <div
        className={twMerge(
          "relative aspect-[3/4] rounded-lg overflow-hidden cursor-pointer transition-all group",
          isSelected && "ring-2 ring-indigo-500 ring-offset-2",
          isLocked && "cursor-not-allowed",
        )}
        onClick={isLocked ? undefined : onClick}
      >
        <div
          className={twMerge(
            "absolute inset-0 bg-cover bg-center",
            isLocked && "grayscale group-hover:grayscale-0 transition-all",
          )}
          style={{ backgroundImage: `url(${preview})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-2 left-2 right-2">
          <Text className="text-white text-xs font-medium truncate block">
            {name}
          </Text>
        </div>
        {isSelected && (
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center">
            <CheckIcon className="w-4 h-4 text-white" />
          </div>
        )}
        {isLocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <div className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <svg
                className="w-5 h-5 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
    </Tooltip>
  );
};

// Questions overlay with falling pictures animation
const QuestionsOverlay = ({ onComplete }: { onComplete: () => void }) => {
  const [showLoading, setShowLoading] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [features, setFeatures] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);

  useEffect(() => {
    setTimeout(() => {
      setShowLoading(true);
    }, 100);
    setTimeout(() => {
      setShowLoading(false);
      setShowQuestions(true);
      posthog.capture("onboarding_questions_shown");
    }, 2500);
  }, []);

  const handleContinue = () => {
    posthog.capture("onboarding_questions_completed", { features, sources });
    saveOnboardingPreferences({ features, sources });
    onComplete();
  };

  return (
    <div className="min-h-dvh w-full bg-white dark:bg-slate-950 flex items-center justify-center">
      <div className="grow p-4 max-w-2xl mx-auto">
        <AnimatePresence>
          {showLoading && (
            <motion.div
              key="loading"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.5 }}
              exit={{ opacity: 0, height: 0 }}
            >
              <FallingPictures pictures={[]} />
              <Spinner className="m-auto z-0" />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showQuestions && (
            <OnboardingQuestions
              features={features}
              sources={sources}
              onFeaturesChange={setFeatures}
              onSourcesChange={setSources}
              onContinue={handleContinue}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
